import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { CP, DOMAIN_COLORS, CapCard, Pill } from "./ui";
import { CaretDown, CaretRight, Plus, FolderSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function TaxonomyAdmin() {
  const { user } = useAuth();
  const [tree, setTree] = useState([]);
  const [openDomains, setOpenDomains] = useState({});
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", domain: "Data & Analytics", description: "", core: false });
  const canEdit = ["ADMIN", "MANAGER"].includes(user?.role);

  const load = () => {
    api.get("/capability/taxonomy").then(({ data }) => {
      setTree(data.domains || []);
      if (data.domains?.length) {
        setOpenDomains({ [data.domains[0].name]: true });
        if (!selected && data.domains[0].children?.length) setSelected(data.domains[0].children[0]);
      }
    }).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const submit = async () => {
    try {
      await api.post("/capability/taxonomy", form);
      toast.success(`Capability "${form.name}" added`);
      setDialogOpen(false);
      setForm({ name: "", domain: "Data & Analytics", description: "", core: false });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="taxonomy-admin">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold" style={{ color: CP.text }}>Capability Taxonomy</div>
          <div className="text-xs" style={{ color: CP.sub }}>Manage your capability framework and relationships</div>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button className="h-9 px-4 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5" style={{ background: CP.blue }} data-testid="taxonomy-add-btn">
                <Plus size={14} weight="bold" /> Add Capability
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Capability</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Capability name"
                  className="w-full h-10 px-3 rounded-lg text-sm border outline-none" data-testid="taxonomy-form-name" />
                <select value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })}
                  className="w-full h-10 px-2 rounded-lg text-sm border outline-none bg-white" data-testid="taxonomy-form-domain">
                  {Object.keys(DOMAIN_COLORS).map((d) => <option key={d}>{d}</option>)}
                </select>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Description" rows={3} className="w-full px-3 py-2 rounded-lg text-sm border outline-none" data-testid="taxonomy-form-desc" />
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.core} onChange={(e) => setForm({ ...form, core: e.target.checked })} data-testid="taxonomy-form-core" />
                  Core capability
                </label>
                <button onClick={submit} disabled={!form.name.trim()} className="h-10 px-5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: CP.navy }} data-testid="taxonomy-form-submit">Create</button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl bg-white shadow-sm p-3 max-h-[600px] overflow-y-auto" style={{ border: `1px solid ${CP.border}` }} data-testid="taxonomy-tree">
          <div className="text-xs font-semibold px-2 py-1.5" style={{ color: CP.sub }}>All Capabilities</div>
          {tree.map((dom) => (
            <div key={dom.name}>
              <button onClick={() => setOpenDomains((o) => ({ ...o, [dom.name]: !o[dom.name] }))}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-50 text-left" data-testid={`taxonomy-domain-${dom.code}`}>
                {openDomains[dom.name] ? <CaretDown size={12} color={CP.sub} /> : <CaretRight size={12} color={CP.sub} />}
                <FolderSimple size={15} color={DOMAIN_COLORS[dom.name] || CP.sky} weight="duotone" />
                <span className="text-sm font-medium" style={{ color: CP.text }}>{dom.code}. {dom.name}</span>
                <span className="ml-auto text-[10px]" style={{ color: CP.sub }}>{dom.children.length}</span>
              </button>
              {openDomains[dom.name] && dom.children.map((c) => (
                <button key={c.cap_id} onClick={() => setSelected(c)}
                  className="w-full text-left pl-9 pr-2 py-1.5 rounded-lg text-sm capitalize hover:bg-slate-50"
                  style={{ color: selected?.cap_id === c.cap_id ? CP.blue : CP.text, fontWeight: selected?.cap_id === c.cap_id ? 600 : 400 }}
                  data-testid={`taxonomy-node-${c.code}`}>
                  {c.code} {c.name}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <CapCard testid="taxonomy-detail"
              title={<span className="capitalize">{selected.name}</span>}
              right={selected.core && <Pill tone="purple">Core Capability</Pill>}>
              <div className="space-y-4">
                <p className="text-sm" style={{ color: CP.sub }}>{selected.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Prop label="Code" value={selected.code} />
                  <Prop label="Parent Domain" value={selected.domain} />
                  <Prop label="Category" value={selected.category} />
                  <Prop label="Status" value={<Pill tone="green">{selected.status}</Pill>} />
                  <Prop label="People" value={selected.people ?? "—"} />
                  <Prop label="Open Demand" value={selected.open_demand ?? "—"} />
                </div>
                <div>
                  <div className="text-xs font-semibold mb-1.5" style={{ color: CP.sub }}>Related Capabilities</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(selected.related || []).map((r) => (
                      <span key={r} className="text-[11px] px-2 py-1 rounded-full capitalize" style={{ background: "#EFF4FF", color: CP.blue }}>{r}</span>
                    ))}
                    {(selected.related || []).length === 0 && <span className="text-xs" style={{ color: CP.sub }}>None linked.</span>}
                  </div>
                </div>
              </div>
            </CapCard>
          ) : <div className="text-sm py-10 text-center" style={{ color: CP.sub }}>Select a capability from the tree.</div>}
        </div>
      </div>
    </div>
  );
}

function Prop({ label, value }) {
  return (
    <div className="rounded-lg p-2.5" style={{ border: `1px solid ${CP.border}` }}>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: CP.sub }}>{label}</div>
      <div className="text-sm font-semibold mt-0.5 capitalize" style={{ color: CP.text }}>{value}</div>
    </div>
  );
}
